// file: src/components/UI/CardList/CardList.jsx

/**
 * RAVEN – UI/CardList
 * A table-like “card row” list that renders rows as Cards and columns via a config array.
 *
 * @component
 * @param {Object}   props
 * @param {string}   props.title
 * @param {React.ReactNode} [props.subtitle]
 * @param {Array<Object>} props.rows
 * @param {string}   [props.keyField="id"]
 * @param {Array<ColumnConfig>} props.columns
 * @param {(row:Object)=>void} [props.onRowClick]
 * @param {Object}   [props.toolbar]
 *        Optional built-in CardList toolbar contract for self-contained callers
 *        that do not inject a full external toolbar.
 * @param {React.ReactNode} [props.toolbarSlot]
 *        Optional full custom toolbar override. When provided, current list
 *        screens typically pass `RecordNav` here instead of the built-in path.
 * @param {{page:number, pageCount:number, onPageChange:(n:number)=>void}} [props.pagination]
 * @param {React.ReactNode} [props.paginationSlot]
 * @param {React.ReactNode} [props.emptyState]
 * @param {(row:Object)=>Array<RowAction>|null} [props.rowActions]
 *        Optional factory returning an array of actions for this row. If it returns
 *        a non-empty array, CardList renders an actions column with a 3-dots menu.
 *
 * @typedef RowAction
 * @property {string} id
 * @property {string} label
 * @property {React.ReactNode} [icon]
 * @property {(row:Object)=>void} onSelect
 * @property {boolean} [disabled]
 * @property {string} [triggerTooltip] Tooltip and accessible label for the row menu trigger.
 * @property {boolean} [danger]   // style hint (e.g., red text)
 *
 * @param {string}   [props.testId="CardList"]
 * @param {"compact"|"comfortable"} [props.density]
 * @param {ResponsiveOptions} [props.responsive]
 * @param {boolean}  [props.showVerticalDividers=true]
 *
 * @typedef ColumnConfig
 * @property {string} key
 * @property {React.ReactNode} title
 * @property {number|string} [w]         // px or "fr" for desktop grid
 * @property {number} [minW]             // min px in responsive grid
 * @property {"left"|"center"|"right"} [align]
 * @property {(row:Object)=>React.ReactNode} [render]
 * @property {"auto"|"always"|((row:Object)=>"auto"|"always")} [tooltip]
 *   // "auto" = only when ellipsized; "always" = even when not ellipsized
 * @property {string|((row:Object)=>string|undefined)} [tooltipText]
 *
 * @typedef ResponsiveOptions
 * @property {"fixed"|"auto"} [mode="fixed"]
 * @property {number} [breakpointPx=1100]
 * @property {number} [splitAfter=3]
 * @property {number} [maxRows=4]
 * @property {number} [minColWidth=120]
 * @property {number} [gap=16]
 */
// RAVEN – UI/CardList
// -----------------------------------------------------------------------------
// Purpose
// A table-like, card-row CardList built on direct Metrea v2 layout/control
// primitives, while preserving the existing responsive card, tooltip, and
// pagination behavior that the surrounding list screens already depend on.
//
// Key ideas
// - Head bar with title + right-aligned controls (Show inactive, Sort By, Results)
// - Rows rendered as Cards with a responsive CSS grid for columns
// - Columns driven by a `columns` config array for consistency across modules
// - Fully controlled: external paging/sorting/filtering via props & callbacks
// - Debug logs follow RAVEN Debug Logging Standard
// - Built-in toolbar controls remain supported for self-contained callers even
//   though the current feature screens usually inject RecordNav via toolbarSlot
//

// UPDATED USAGE — Baselines page
/*
	<CardList
		title="Baselines"
		rows={baselines} // array of row objects
		keyField="id"
		density="comfortable"
		showVerticalDividers // optional (default: true); keep table-like look
		// ───────────────────────────────── Columns ─────────────────────────────────
		columns={[
			{
				key: "name",
				title: "Name",
				w: 360, // desktop width; responsive will use minW/stackGrow
				stackGrow: 2, // allow Name to take more space in stacked layout
				render: (row) => row.name,

				// Name tooltip rule:
				// - if description has text → show custom tooltip even if not truncated
				// - else → show tooltip only when truncated
				tooltip: (row) => (row.description?.trim() ? "always" : "auto"),
				tooltipText: (row) =>
					row.description?.trim() ? `${row.name}\n\nNote:\n${row.description}` : undefined, // important: no empty bubble
			},
			{
				key: "periodLabel",
				title: "Period",
				minW: 100,
				tooltip: "auto",
				render: (row) => row.periodLabel,
			},
			{
				key: "currency",
				title: "Currency Symbol",
				minW: 120,
				tooltip: "auto",
				render: (row) => `${row.currency_code} - ${row.currency_symbol} ${row.currency_unit}`,
			},
			{ key: "createdAt", title: "Date Created", minW: 100, tooltip: "auto" },
			{ key: "updatedAt", title: "Last Updated", minW: 120, tooltip: "auto" },
			{ key: "scenarios", title: "Scenarios #", minW: 80, align: "center", tooltip: "auto" },
			{ key: "activeItems", title: "Active Items #", minW: 100, align: "center", tooltip: "auto" },

			// Example control cell (Select). Non-text cells don’t get the ellipsis tooltip path.
			{
				key: "status",
				title: "Status",
				minW: 140,
				render: (row) => (
					<Select
						size="sm"
						valueId={row.status}
						onChange={(opt) => onChangeRow?.(row, { status: opt.id })}
						options={[
							{ id: "active", label: "Active" },
							{ id: "inactive", label: "Inactive" },
						]}
					/>
				),
			},
		]}
		// ─────────────────────────────── Toolbar ────────────────────────────────
		toolbar={{
			showInactive: { value: showInactive, onChange: setShowInactive },
			sortBy: {
				valueId: sortBy,
				onChange: (opt) => setSortBy(opt.id),
				options: [
					{ id: "updatedAt:desc", label: "Update Date (desc)" },
					{ id: "updatedAt:asc", label: "Update Date (asc)" },
					{ id: "createdAt:desc", label: "Create Date (desc)" },
				],
			},
			pageSize: {
				valueId: String(pageSize),
				onChange: (opt) => setPageSize(Number(opt.id)),
				options: [
					{ id: "10", label: "10" },
					{ id: "25", label: "25" },
					{ id: "50", label: "50" },
				],
			},
		}}
		// ─────────────────────────── Pagination (external) ───────────────────────────
		// CardList is a direct Metrea v2 path; keep paging callbacks on the simple
		// onPageChange(nextPage) contract instead of preserving older pagination signatures.
		pagination={{
			page,
			pageCount: Math.ceil(total / pageSize),
			onPageChange,
		}}
		// (Optional) custom pager slot — e.g., using metrea-ui-v2 Pagination
		// paginationSlot={
		//   <Pagination.Root count={total} pageSize={pageSize} page={page} onPageChange={({ page }) => onPageChange(page)}>
		//     <ButtonGroup variant="ghost" size="2xs">
		//       <Pagination.PrevTrigger asChild><IconButton icon={<ChevronLeftRegular />} /></Pagination.PrevTrigger>
		//       <Pagination.Items render={(p) => <IconButton variant={{ base: "ghost", _selected: "solid" }}>{p.value}</IconButton>} />
		//       <Pagination.NextTrigger asChild><IconButton icon={<ChevronRightRegular />} /></Pagination.NextTrigger>
		//     </ButtonGroup>
		//   </Pagination.Root>
		// }

		// ───────────────────────────── Responsive layout ─────────────────────────────
		// AUTO: wrap into multiple rows when columns won’t fit; respects minW & gap.
		responsive={{
			mode: "auto",
			maxRows: 5,
			minColWidth: 120, // floor for columns without explicit minW
			gap: 12, // column gap
		}}
		// Alternative (FIXED) if you want a hard breakpoint + split:
		// responsive={{ mode: "fixed", breakpointPx: 1100, splitAfter: 4 }}

		// Row navigation
		onRowClick={(row) => navigate(`/baseline/${row.id}`)}

		// (Optional) empty state
		// emptyState={<Box p={4} color="muted">No baselines found.</Box>}
	/>;
*/
// -----------------------------------------------------------------------------

import React, { useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";

import { verticalDivider, horizontalDivider } from "./dividers";
import {
	Box,
	createListCollection,
	IconButton,
	Menu,
	MetreaSelectContent,
	MetreaSelectItem,
	MetreaSelectRoot,
	MetreaSelectTrigger,
	MetreaSelectValueText,
	Portal,
	Switch,
	Text,
	MetreaTooltip,
} from "@metrea-ui/v2";
import { MoreVerticalFilled } from "@fluentui/react-icons";

// Utils
import { debugLog } from "src/utils/debugUtils";
import { mColor } from "src/utils/themeUtils";

/**
 * Used to reserve the value row's height when a cell has no data.
 * It's visually blank but still occupies line-height for alignment.
 */
const EMPTY_PLACEHOLDER = "\u00A0"; // non-breaking space

const PHOTO_RAIL_W = 110; // px – same value as old baseline
const CARD_MIN_HEIGHT_W_PHOTO = "90px";
const ACTION_RAIL_W = 36; // fixed width for the right rail (button + a little air)
const ACTION_RAIL_GAP_Y = 2; // top/bottom gap that matches your other dividers (4px)
// Direct Metrea Box interprets numeric spacing props as design-system tokens.
// Keep explicit px strings where CardList still needs the legacy raw-pixel density.
const CARD_ROW_MARGIN_BOTTOM = "4px";
const CARD_SURFACE_PADDING = "1px";
const CARD_IMAGE_PADDING_X = "1px";
const CARD_SHIFTED_GRID_COLUMN_GAP = "8px";
const CARD_SHIFTED_GRID_ROW_GAP = "4px";
const CARD_DESKTOP_GRID_COLUMN_GAP = "6px";
const CARD_DESKTOP_GRID_ROW_GAP = "3px";
const CARD_DESKTOP_GRID_PADDING_X = "2px";
const CARD_DESKTOP_GRID_PADDING_Y_COMPACT = "0.75px";
const CARD_DESKTOP_GRID_PADDING_Y_COMFORTABLE = "1px";
const CARD_MULTIROW_PADDING_X = "1px";
const CARD_MULTIROW_PADDING_RIGHT = `${ACTION_RAIL_W / 2}px`;
const CARD_MULTIROW_PADDING_Y_COMPACT = "0.5px";
const CARD_MULTIROW_PADDING_Y_COMFORTABLE = "0.75px";
const CARD_MULTIROW_SECTION_PADDING = "1px";
const CARD_MULTIROW_GROUP_COLUMN_GAP = "8px";

const sizeToSelectSize = (size) => {
	if (size === "large") return "md";
	if (size === "small") return "sm";
	if (size === "xs" || size === "2xs") return "xs";
	return size || "sm";
};

const getSelectLabel = (option) => option?.label ?? option?.name ?? String(option?.id ?? "");

const getSelectValue = (option) => String(option?.id ?? option?.value ?? "");

const buildSelectCollectionItems = (options) =>
	(options ?? []).map((option) => ({
		label: getSelectLabel(option),
		value: getSelectValue(option),
		disabled: option?.disabled,
	}));

const buildSelectCollection = (options) =>
	createListCollection({ items: buildSelectCollectionItems(options ?? []) });

// Vertical divider style for grid children
const gridDividers = (color) => ({
	"& > *:not(:first-of-type)": {
		position: "relative",
		paddingLeft: "12px",
		borderLeft: "none",

		"&::before": {
			content: '""',
			position: "absolute",
			left: 0,
			top: `${ACTION_RAIL_GAP_Y}px`,
			bottom: `${ACTION_RAIL_GAP_Y}px`,
			width: "1px",
			backgroundColor: color,
			opacity: 0.9,
		},
	},
	"& > *:last-of-type": { paddingRight: "12px" },
});

// Shared helper for the handful of action/tool button cases that still need a tooltip.
const renderTooltip = (content, placement, children) => {
	if (!content) return children;
	const positioning = placement && placement !== "bottom" ? { placement } : undefined;
	return (
		<MetreaTooltip content={content} positioning={positioning}>
			{children}
		</MetreaTooltip>
	);
};

const renderIconButtonWithTooltip = ({
	tooltip,
	placement = "bottom",
	cursor = "pointer",
	disabled = false,
	style,
	children,
	...buttonProps
}) => {
	const button = (
		<IconButton
			{...buttonProps}
			disabled={disabled}
			aria-disabled={disabled || undefined}
			tabIndex={disabled ? -1 : undefined}
			onClick={disabled ? undefined : buttonProps.onClick}
			style={
				disabled
					? {
							cursor: "not-allowed",
							opacity: 0.5,
							...(style || {}),
						}
					: style
			}
		>
			{children}
		</IconButton>
	);
	if (!tooltip) return button;

	return renderTooltip(
		tooltip,
		placement,
		<span style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", cursor }}>
			{button}
		</span>,
	);
};

// Keep the existing CardList toolbar contract stable: callers still receive the
// original option object instead of a raw DOM string from the native select.
const findSelectedOption = (options, rawValue) =>
	(options ?? []).find((option) => String(option?.id) === String(rawValue)) ??
	(options ?? []).find((option) => String(option?.value) === String(rawValue)) ??
	null;

const getSwitchValue = (next) =>
	typeof next === "boolean"
		? next
		: (next?.target?.checked ?? next?.checked ?? next?.detail?.checked ?? next?.value ?? false);

/**
 * EllipsisTooltip
 * - Default: render a tooltip **only when** the child text is actually truncated.
 * - Special cases (e.g., Name + Description): set `always=true` to force tooltip even when not truncated.
 * - Never renders a tooltip when `text` is empty/whitespace (prevents “empty bubble”).
 *
 * Implementation notes:
 * - Uses a ResizeObserver to keep `isTruncated` accurate on container resizes.
 * - Uses `whiteSpace: "pre-line"` so `\n` renders as line breaks.
 *
 * @param {Object}   params
 * @param {string}   params.text
 * @param {"top"|"bottom"|"left"|"right"} [params.placement="bottom"]
 * @param {function(RefObject, {onMouseEnter:Function, onMouseLeave:Function}):ReactNode} params.childrenWithRef
 *        A render prop that receives a ref and hover handlers and must attach them to the text node.
 * @param {boolean}  [params.always=false]
 */
function EllipsisTooltip({ text, placement = "bottom", childrenWithRef, always = false }) {
	const tooltipText = (text ?? "").toString();
	const hasTooltipText = tooltipText.trim().length > 0;

	const ref = useRef(null);
	const [isTruncated, setIsTruncated] = useState(false);

	// Re-measure whenever size or text changes
	const measureOverflow = () => {
		const el = ref.current;
		if (!el) return;
		// small epsilon avoids false negatives due to fractional metrics
		setIsTruncated(el.scrollWidth - el.clientWidth > 0.5);
	};

	// -------------------------------------------------------
	// Initial measure + keep fresh during resizes
	// Measure at mount and when text changes; keep it fresh on resize.
	// -------------------------------------------------------
	React.useLayoutEffect(() => {
		measureOverflow();
		const el = ref.current;
		if (!el || typeof ResizeObserver === "undefined") return;
		const ro = new ResizeObserver(measureOverflow);
		ro.observe(el);
		return () => ro.disconnect();
	}, [tooltipText]);

	const child = childrenWithRef(ref, {
		onMouseEnter: measureOverflow,
		onMouseLeave: () => setIsTruncated(false), // optional: collapse state after hover
	});

	// Guard 1: no text ⇒ no tooltip wrapper at all
	if (!hasTooltipText) return child;

	// Guard 2: overflow-only mode ⇒ only wrap when actually truncated
	if (!always && !isTruncated) return child;

	// OK to render tooltip (multiline is enabled via whiteSpace: "pre-line")
	return (
		<MetreaTooltip
			content={
				<div style={{ whiteSpace: "pre-line", wordBreak: "break-word", maxWidth: "480px" }}>
					{tooltipText}
				</div>
			}
			positioning={placement && placement !== "bottom" ? { placement } : undefined}
		>
			{child}
		</MetreaTooltip>
	);
}

/**
 * RowActionsMenu
 * - Renders a 3-dots button and a contextual menu for a given row.
 * - Stops event propagation so it never triggers the row's onClick navigation.
 */
function RowActionsMenu({ actions = [], row }) {
	if (!actions?.length) return null;
	const triggerTooltip = actions.find(
		(action) => typeof action?.triggerTooltip === "string",
	)?.triggerTooltip;

	// Stop bubbling to the Card on both mouse & keyboard paths
	const stop = (e) => e.stopPropagation();
	const menuTrigger = (
		<Menu.Trigger asChild>
			<IconButton
				aria-label={triggerTooltip || "Open row actions"}
				data-testid={`row-actions-trigger-${row.id}`}
				onClick={stop}
				onMouseDown={stop}
				onKeyDown={(e) => {
					if (e.key === "Enter" || e.key === " ") stop(e);
				}}
				variant="ghost"
				size="xs"
				w="28px"
				h="28px"
				minW="28px"
				minH="28px"
				display="inline-flex"
				alignItems="center"
				justifyContent="center"
				opacity={0.55}
				transition="opacity 120ms"
				outline="none"
				boxShadow="none"
				_hover={{ opacity: 1 }}
				_focusVisible={{ opacity: 1 }}
				_active={{ transform: "none" }}
			>
				<MoreVerticalFilled />
			</IconButton>
		</Menu.Trigger>
	);
	const menuTriggerWithTooltip = triggerTooltip
		? renderTooltip(
				triggerTooltip,
				"left",
				<span style={{ display: "inline-flex", alignItems: "center", justifyContent: "center" }}>
					{menuTrigger}
				</span>,
			)
		: menuTrigger;

	return (
		<Menu.Root>
			{menuTriggerWithTooltip}
			<Portal>
				<Menu.Positioner>
					<Menu.Content onClick={stop} onMouseDown={stop} onKeyDown={stop}>
						{actions.map((a) => {
							if (a?.type === "separator" || a?.id === "divider") {
								return <Menu.Separator key={a.id || `separator-${row.id}`} />;
							}

							return (
								<Menu.Item
									key={a.id}
									data-testid={`row-action-${a.id}-${row.id}`}
									disabled={a.disabled}
									style={a.danger ? { color: "#c62828" } : undefined}
									onClick={(e) => {
										e.stopPropagation();
										a.onSelect?.(row);
									}}
								>
									{a.icon ? (
										<span
											style={{
												marginRight: "8px",
												display: "inline-flex",
												alignItems: "center",
											}}
										>
											{a.icon}
										</span>
									) : null}
									{a.label}
								</Menu.Item>
							);
						})}
					</Menu.Content>
				</Menu.Positioner>
			</Portal>
		</Menu.Root>
	);
}

// Compute a CSS grid template given column widths. Accepts px numbers or CSS strings.
function buildGridTemplate(columns) {
	const parts = columns.map((c) => {
		if (c.w) return typeof c.w === "number" ? `${c.w}px` : String(c.w);
		if (c.minW) return `minmax(${c.minW}px, 1fr)`;
		return `minmax(120px, 1fr)`;
	});
	return parts.join(" ");
}

/**
 * Build the responsive grid template for a row group.
 * We honor each column's floor (`minW`) so labels like “Currency Symbol” don’t collapse,
 * while allowing the column to grow using either a fixed pixel width, an explicit "fr" ratio,
 * or a generic `stackGrow` → `Nfr`.
 *
 * @param {Array<ColumnConfig>} cols
 * @returns {string} CSS grid-template-columns value
 */
const buildResponsiveTemplate = (cols) =>
	cols
		.map((c) => {
			const min = Number.isFinite(c?.minW) ? `${c.minW}px` : "0";
			// fixed pixel width
			if (typeof c?.w === "number") return `minmax(${c.w}px, ${c.w}px)`;
			// explicit fractional width (preserve ratio) but still honor a floor
			if (typeof c?.stackW === "string" && c.stackW.trim().endsWith("fr")) {
				return `minmax(${min}, ${c.stackW.trim()})`;
			}
			if (typeof c?.w === "string" && c.w.trim().endsWith("fr")) {
				return `minmax(${min}, ${c.w.trim()})`;
			}
			// default: grow by stackGrow (or 1fr) with a min floor
			const grow = Number.isFinite(c?.stackGrow) ? c.stackGrow : 1;
			return `minmax(${min}, ${grow}fr)`;
		})
		.join(" ");

export default function CardList({
	title,
	subtitle,
	rows = [],
	keyField = "id",
	columns = [],
	onRowClick,
	onChangeRow,
	toolbar,
	toolbarSlot, // React node (e.g., <RecordNav .../>)
	pagination,
	paginationSlot, // React node (e.g., <Pagination .../>)
	emptyState,
	testId = "CardList",
	showLabels = false,
	density = "compact", // 'compact' | 'comfortable'
	responsive = { mode: "fixed", breakpointPx: 1100, splitAfter: 3, maxRows: 4, minColWidth: 120, gap: 16 },
	showVerticalDividers = true,
	dividerColor = "rgba(0,0,0,0.12)",
	rowActions,
	photoColumn,
}) {
	const containerRef = React.useRef(null);
	const [containerW, setContainerW] = React.useState(0);
	const renderCount = useRef(0);
	// Capture initial props for the true mount log; live prop changes are reported by the render log below.
	const mountDebug = useRef({ title, rows: rows.length });
	renderCount.current++;

	useEffect(() => {
		debugLog?.(2, "CardList mount", mountDebug.current, { module: "CardList" });
	}, []);

	React.useLayoutEffect(() => {
		if (!containerRef.current) return;
		const ro = new ResizeObserver((entries) => {
			for (const e of entries) setContainerW(Math.floor(e.contentRect.width));
		});
		ro.observe(containerRef.current);
		return () => ro.disconnect();
	}, []);

	// ── Derived layout -----------------------------------------------------------
	const gridTemplate = useMemo(() => buildGridTemplate(columns), [columns]);

	// type scale
	const fontSizes =
		density === "compact"
			? { header: "0.8125rem", label: "0.6875rem", value: "0.7875rem" }
			: { header: "0.875rem", label: "0.75rem", value: "0.85rem" };

	// ── Debug (render) ----------------------------------------------------------
	debugLog?.(
		9,
		"CardList render",
		{ title, rows: rows.length, renderCount: renderCount.current },
		{ module: "CardList" },
	);

	// ── Render helpers ----------------------------------------------------------
	const renderCell = (col, row) => {
		const value = col.render ? col.render(row) : row?.[col.key];
		const align = col.align || "left";
		const isPlain =
			(typeof value === "string" || typeof value === "number") && value !== null && value !== undefined;
		const isInlineRenderable = isPlain || React.isValidElement(value) || Array.isArray(value);

		// -------------------------------------------------------
		// Decide how we want tooltips per cell (readable names):
		// - "auto": tooltip only when truncated (default behavior elsewhere)
		// - "always": tooltip even when not truncated (used by Name when description exists)
		// -------------------------------------------------------
		const tooltipModeForCell = typeof col.tooltip === "function" ? col.tooltip(row) : col.tooltip;
		const tooltipOnlyIfTruncated = tooltipModeForCell === "auto";
		const tooltipEvenIfNotTruncated = tooltipModeForCell === "always";
		const rawCellValue = row?.[col.key];

		// -------------------------------------------------------
		// Get tooltip text for this cell (string or function(row)).
		// If it resolves to empty/undefined, EllipsisTooltip will skip rendering.
		// -------------------------------------------------------
		// Keep non-primitive render output off the implicit String(value) fallback so
		// highlighted/text-like React content does not degrade into "[object Object]".
		const explicitTooltipText =
			typeof col.tooltipText === "function" ? col.tooltipText(row) : col.tooltipText;
		const tooltipTextFallback =
			isPlain || typeof rawCellValue === "string" || typeof rawCellValue === "number"
				? String(isPlain ? value : rawCellValue)
				: undefined;
		const tooltipTextForCell = explicitTooltipText ?? tooltipTextFallback;

		// -------------------------------------------------------
		// helper to build the actual text box, with optional ref/handlers for the tooltip
		// Use a non-breaking space when the value is empty so the “value row” always
		// reserves the same height. This keeps header labels and values perfectly aligned
		// even when some fields are null/empty.
		// -------------------------------------------------------
		const makeValueBox = (content, refProps = {}) => {
			const displayContent =
				content === null ||
				content === undefined ||
				(typeof content === "string" && content.trim() === "")
					? EMPTY_PLACEHOLDER
					: content;
			return (
				// Keep the value shell explicit so the migrated CardList layout keeps
				// the old raw-pixel density while staying on direct Metrea primitives.
				<Box
					ref={refProps.ref}
					onMouseEnter={refProps.onMouseEnter}
					onMouseLeave={refProps.onMouseLeave}
					fontSize={fontSizes.value}
					color={mColor.StonyBlue}
					lineHeight="1.25rem"
					minH="1.25rem"
					overflow="hidden"
					textOverflow="ellipsis"
					whiteSpace="nowrap"
				>
					{displayContent}
				</Box>
			);
		};

		return (
			<Box as="div" key={`c-${col.key}`} data-col-key={col.key} textAlign={align} minW={0}>
				{showLabels && (
					<Text
						as="div"
						fontSize={fontSizes.label}
						opacity={0.65}
						mb="0.125rem"
						overflow="hidden"
						textOverflow="ellipsis"
						whiteSpace="nowrap"
					>
						{col.title}
					</Text>
				)}

				{/* Text-like inline content: overflow-only or always-on tooltip depending on column config */}
				{isInlineRenderable && (tooltipOnlyIfTruncated || tooltipEvenIfNotTruncated) ? (
					<EllipsisTooltip
						text={tooltipTextForCell}
						always={tooltipEvenIfNotTruncated}
						childrenWithRef={(ref, handlers) => makeValueBox(value, { ref, ...handlers })}
					/>
				) : isPlain ? (
					makeValueBox(value)
				) : (
					// Non-text (controls/components) → NO clamping so nothing is cut off
					<Box
						fontSize={fontSizes.value}
						overflow="visible"
						whiteSpace="normal"
						color={mColor.StonyBlue}
						lineHeight="1.25rem"
						minH="1.25rem"
					>
						{value}
					</Box>
				)}
			</Box>
		);
	};

	const hasToolbar = Boolean(toolbar);
	const page = pagination?.page ?? 1;
	const pageCount = pagination?.pageCount ?? 1;

	const renderToolbarSelect = (config, fallbackOptions, minW, name, debugKey) => {
		if (!config) return null;

		const options = config.options ?? fallbackOptions ?? [];
		const selectedValue = String(config.valueId ?? options[0]?.id ?? "");
		// Preserve the built-in CardList toolbar API for lightweight callers even
		// though the current feature screens usually provide RecordNav via toolbarSlot.
		const collection = buildSelectCollection(options);

		return (
			<div style={{ minWidth: minW }}>
				<MetreaSelectRoot
					variant="outline"
					name={name}
					collection={collection}
					size={sizeToSelectSize("sm")}
					width="100%"
					value={[selectedValue]}
					onValueChange={(details) => {
						const rawValue = Array.isArray(details?.value) ? String(details.value[0] ?? "") : "";
						// Preserve the existing CardList toolbar contract: callers still
						// receive the matched option object instead of a raw selected value.
						const selectedOption = findSelectedOption(options, rawValue);
						debugLog?.(
							4,
							`change ${debugKey}`,
							{ id: selectedOption?.id ?? rawValue },
							{ module: "CardList" },
						);
						if (selectedOption) config.onChange?.(selectedOption);
					}}
				>
					<MetreaSelectTrigger>
						<MetreaSelectValueText>
							{(selectedItems) => getSelectLabel(selectedItems[0])}
						</MetreaSelectValueText>
					</MetreaSelectTrigger>
					<Portal>
						<MetreaSelectContent>
							{collection.items.map((item) => (
								<MetreaSelectItem key={item.value} item={item} disabled={item.disabled}>
									{item.label}
								</MetreaSelectItem>
							))}
						</MetreaSelectContent>
					</Portal>
				</MetreaSelectRoot>
			</div>
		);
	};

	// Toolbar pieces -------------------------------------------------------------
	const ShowInactive =
		hasToolbar && toolbar?.showInactive ? (
			<div style={{ display: "inline-flex", alignItems: "center", gap: "8px" }}>
				<Switch.Root
					checked={!!toolbar.showInactive.value}
					onCheckedChange={(next) => {
						const checked = getSwitchValue(next);
						debugLog?.(4, "toggle showInactive", { next: checked }, { module: "CardList" });
						toolbar.showInactive.onChange?.(checked);
					}}
				>
					<Switch.HiddenInput />
					<Switch.Control>
						<Switch.Thumb />
					</Switch.Control>
				</Switch.Root>
				<Text as="span">{toolbar.showInactive.label ?? "Show inactive"}</Text>
			</div>
		) : null;

	const SortBy =
		hasToolbar && toolbar?.sortBy
			? renderToolbarSelect(toolbar.sortBy, [], "220px", "SortBy", "sortBy")
			: null;

	const PageSize =
		hasToolbar && toolbar?.pageSize
			? renderToolbarSelect(
					{
						...toolbar.pageSize,
						valueId: String(toolbar.pageSize.valueId ?? 10),
					},
					[
						{ id: "10", label: "10" },
						{ id: "25", label: "25" },
						{ id: "50", label: "50" },
					],
					"120px",
					"PageSize",
					"pageSize",
				)
			: null;

	// Action buttons (optional, right side). Expect [{icon, label, onClick}]
	const Actions =
		hasToolbar && Array.isArray(toolbar?.actions) && toolbar.actions.length > 0 ? (
			<div style={{ display: "inline-flex", gap: "8px" }}>
				{toolbar.actions.map((a, i) => (
					<React.Fragment key={`act-${i}`}>
						{renderIconButtonWithTooltip({
							tooltip: a.label,
							placement: "bottom",
							"aria-label": typeof a.label === "string" ? a.label : "Toolbar action",
							size: "md",
							variant: "ghost",
							onClick: a.onClick,
							children: a.icon,
						})}
					</React.Fragment>
				))}
			</div>
		) : null;

	// Responsive helpers ---------------------------------------------------------
	// const bp = responsive?.breakpointPx ?? 1100;
	let bp = responsive?.breakpointPx ?? 1100;
	if (typeof photoColumn === "function") {
		bp = bp - (PHOTO_RAIL_W + ACTION_RAIL_W + 100);
		// PHOTO_RAIL_W = 110
		// ACTION_RAIL_W = 36
		// +100 for extra padding + column-gap + divider offsets
	}
	const splitAfter = Math.max(1, Math.min(columns.length - 1, responsive?.splitAfter ?? 3));
	const firstCols = columns.slice(0, splitAfter);
	const secondCols = columns.slice(splitAfter);

	const toPx = (v, fallback = 160) => {
		if (v == null) return fallback;
		if (typeof v === "number") return v;
		const m = String(v).match(/\d+(\.\d+)?/);
		return m ? Number(m[0]) : fallback;
	};
	// -------------------------------------------------------
	// Auto-wrap columns into up to `maxRows` responsive rows.
	// We intentionally break a bit **before** the true limit (90% of container width)
	// to avoid the “wrap happens too late” feel and to keep Select chevrons/focus rings visible.
	// -------------------------------------------------------
	const autoGroups = React.useMemo(() => {
		if (responsive?.mode !== "auto" || !containerW) return null;

		const gap = responsive?.gap ?? 16;
		const pad = 24 * 2; // px padding from px: 3 on left + right
		const maxRows = Math.max(1, responsive?.maxRows ?? 4);
		const minColWidth = responsive?.minColWidth ?? 140;
		const prefW = (col) => (col.w ? toPx(col.w, minColWidth) : col.minW ? col.minW : minColWidth);

		const groups = [[]];
		let rowWidth = pad;

		columns.forEach((c) => {
			const need = prefW(c, minColWidth) + (groups[groups.length - 1].length ? gap : 0);

			// Trigger wrap earlier to avoid the "wraps too late" feel.
			// const earlyBreak = containerW - 100; // start stacking when within 100px margin
			const earlyBreak = containerW * 0.84; // start a new row once the current row would exceed ~90% of container width

			// if (rowWidth + need > containerW && groups.length < maxRows) {
			if (rowWidth + need > earlyBreak && groups.length < maxRows) {
				groups.push([c]);
				rowWidth = pad + prefW(c, minColWidth);
			} else {
				groups[groups.length - 1].push(c);
				rowWidth += need;
			}
		});

		// return groups; // e.g., [[c1,c2,c3],[c4,c5],[c6]]
		// If only one group is produced, disable multi-row mode
		// (desktop layout should render instead)
		return groups.length > 1 ? groups : null;
	}, [
		columns,
		containerW,
		responsive?.mode,
		responsive?.gap,
		responsive?.maxRows,
		responsive?.minColWidth,
	]);

	/**
	 * Emit a page change using the direct CardList paging contract.
	 */
	const emitPageChange = (newPage) => {
		if (!pagination?.onPageChange) return;
		const n = Math.max(1, Math.min(pageCount || 1, Number(newPage) || 1));
		pagination.onPageChange(n);
	};

	// Prevent clipping of right-edge controls (Select, chevron, focus ring)
	// The container should allow overflow so inner Card contents can fully render.
	return (
		// Allow right-edge focus ring/chevrons to render outside the card clip box.
		// Card container overflow: keep Select chevrons from clipping.
		<div ref={containerRef} data-testid={testId} style={{ width: "100%", overflow: "visible" }}>
			{/* Header Bar */}
			<div
				style={{
					display: "flex",
					alignItems: "center",
					justifyContent: "space-between",
					// Keep the embedded RecordNav close to the first card without
					// squeezing the toolbar's own label/control spacing.
					marginBottom: "2px",
				}}
			>
				<div>
					{title && (
						<Text as="h4" fontWeight={700} lineHeight="1.2" fontSize="1rem">
							{title}
						</Text>
					)}
					{subtitle && (
						<Text as="div" opacity={0.75} fontSize="0.85rem">
							{subtitle}
						</Text>
					)}
				</div>
				{toolbarSlot ? (
					<div style={{ minWidth: 0, width: "100%" }}>{toolbarSlot}</div>
				) : (
					hasToolbar && (
						<div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
							{ShowInactive}
							{SortBy}
							{PageSize}
							{Actions}
						</div>
					)
				)}
			</div>

			{/* Rows */}
			{rows.length === 0 &&
				(emptyState || (
					<div style={{ padding: "24px 12px", textAlign: "center", opacity: 0.7 }}>No results.</div>
				))}

			{rows.map((row, idx) => {
				const key = row?.[keyField] ?? idx;
				const rowActionItems = typeof rowActions === "function" ? rowActions(row) || [] : [];
				const hasRowActions = rowActionItems.length > 0;

				return (
					<Box
						key={key}
						data-testid={`card-row-${row.id}`}
						as="div"
						role="group"
						bg="white"
						border="1px solid rgba(0,0,0,0.12)"
						borderLeft={`3px solid ${mColor.MidnightBlue}`}
						borderTop={`1px solid ${dividerColor}`}
						borderRadius="sm"
						boxShadow="sm"
						_hover={{ boxShadow: "md" }}
						transition="box-shadow .15s ease, transform .15s ease"
						overflow="visible"
						cursor={onRowClick ? "pointer" : "default"}
						p={0}
						mb={CARD_ROW_MARGIN_BOTTOM}
					>
						{/* Outer surface wrapper */}
						<Box
							className="lv-surface"
							p={CARD_SURFACE_PADDING}
							pl={typeof photoColumn === "function" ? `${PHOTO_RAIL_W}px` : "4px"}
							pr={`${ACTION_RAIL_W}px`}
							position="relative"
							overflow="visible"
							transition="background-color .15s ease"
							bg="transparent"
							_hover={{ bg: "rgba(198,214,228,0.35)" }}
							minH={typeof photoColumn === "function" ? CARD_MIN_HEIGHT_W_PHOTO : "auto"}
							display="flex"
							alignItems="stretch"
						>
							{/* Photo rail (left of card, optional) */}
							{typeof photoColumn === "function" && (
								<Box
									className="photo-rail"
									position="absolute"
									top={ACTION_RAIL_GAP_Y}
									bottom={ACTION_RAIL_GAP_Y}
									left={0}
									w={`${PHOTO_RAIL_W}px`}
									display="flex"
									alignItems="center"
									justifyContent="center"
									bg="transparent"
									zIndex={2}
									pointerEvents="none"
									_before={verticalDivider(dividerColor, ACTION_RAIL_GAP_Y, "right")}
								>
									<Box
										pointerEvents="auto"
										display="flex"
										alignItems="center"
										justifyContent="center"
										w="100%"
										h="100%"
										px={CARD_IMAGE_PADDING_X}
										css={{
											"img, svg": {
												maxWidth: "95%", // bigger image as requested
												maxHeight: "95%",
												objectFit: "contain", // prevents portrait overflow
												margin: "auto",
												display: "block",
											},
										}}
									>
										{photoColumn(row)}
									</Box>
								</Box>
							)}

							{/* Shifted grid content when photo column present */}
							<Box
								className="shift_grid_for_photo"
								pl={typeof photoColumn === "function" ? "4px" : "0px"}
								display="flex"
								flexDir="column"
								columnGap={CARD_SHIFTED_GRID_COLUMN_GAP}
								rowGap={CARD_SHIFTED_GRID_ROW_GAP}
								w="100%"
							>
								{/* Desktop/table grid — show only when NOT using auto-groups */}
								<Box
									className="desktop_layout"
									display={
										responsive?.mode === "fixed" ? "grid" : !autoGroups ? "grid" : "none"
									}
									gridTemplateColumns={gridTemplate}
									columnGap={CARD_DESKTOP_GRID_COLUMN_GAP} // solves left/right column offset drift
									rowGap={CARD_DESKTOP_GRID_ROW_GAP}
									alignItems="stretch"
									h="100%"
									// exact padding to match both rails
									px={CARD_DESKTOP_GRID_PADDING_X}
									py={
										density === "compact"
											? CARD_DESKTOP_GRID_PADDING_Y_COMPACT
											: CARD_DESKTOP_GRID_PADDING_Y_COMFORTABLE
									}
									position="relative"
									_before={undefined} // ensure no duplicate pseudo elements
									// improved vertical dividers aligned to rails
									css={{
										"& > *:not(:first-of-type)": {
											position: "relative",
											paddingLeft: "12px",
										},
										"& > *:not(:first-of-type)::before": verticalDivider(
											dividerColor,
											ACTION_RAIL_GAP_Y,
											"left",
										),
										"& > *:last-of-type": {
											paddingRight: "12px",
										},
									}}
								>
									{columns.map((c) => (
										// Grid child minWidth: 0: allows ellipsis & proper overflow measurement.
										<Box
											key={`${key}-${c.key}`}
											data-col-key={c.key}
											minW={0}
											display="flex"
											alignItems="center"
											h="100%" // stretch divider with card’
										>
											{renderCell(c, row)}
										</Box>
									))}
								</Box>

								{/* Responsive multi-row layout */}
								<Box
									className="multirow_layout"
									display={
										responsive?.mode === "auto" ? (autoGroups ? "grid" : "none") : "none"
									}
									__css={
										responsive?.mode === "fixed"
											? { [`@media (max-width: ${bp}px)`]: { display: "grid" } }
											: {}
									}
									rowGap={0}
									px={CARD_MULTIROW_PADDING_X}
									pr={CARD_MULTIROW_PADDING_RIGHT}
									py={
										density === "compact"
											? CARD_MULTIROW_PADDING_Y_COMPACT
											: CARD_MULTIROW_PADDING_Y_COMFORTABLE
									}
									w="100%"
								>
									{(() => {
										const groupsForRender =
											autoGroups ?? [firstCols, secondCols].filter((g) => g.length > 0);
										return groupsForRender.map((grp, gi) => (
											<Box
												key={`rg-${gi}`}
												pt={gi > 0 ? CARD_MULTIROW_SECTION_PADDING : 0}
												pb={gi > 0 ? CARD_MULTIROW_SECTION_PADDING : 0}
												mt={gi > 0 ? CARD_MULTIROW_SECTION_PADDING : 0}
												borderTop={gi > 0 ? `1px solid ${dividerColor}` : undefined}
												position="relative"
												_before={gi > 0 ? horizontalDivider(dividerColor) : undefined}
											>
												<Box
													display="grid"
													gridTemplateColumns={buildResponsiveTemplate(grp)}
													columnGap={CARD_MULTIROW_GROUP_COLUMN_GAP}
													alignItems="center"
													{...(showVerticalDividers
														? gridDividers(dividerColor)
														: {})}
												>
													{grp.map((c) => (
														<Box
															key={`rgc-${gi}-${c.key}`}
															data-col-key={c.key}
															minW={0}
														>
															{renderCell(c, row)}
														</Box>
													))}
												</Box>
											</Box>
										));
									})()}
								</Box>
							</Box>

							{/* Row actions menu (anchored to right edge of the card) */}
							{/* Right-edge actions rail: full card height minus gaps, with a vertical divider. */}
							{typeof rowActions === "function" && hasRowActions && (
								<Box
									position="absolute"
									top={ACTION_RAIL_GAP_Y}
									bottom={ACTION_RAIL_GAP_Y}
									right={0}
									w={`${ACTION_RAIL_W}px`}
									display="flex"
									alignItems="center"
									justifyContent="center"
									borderLeft={`1px solid ${dividerColor}`}
									bg="transparent"
									zIndex={2}
									pointerEvents="none"
									_before={verticalDivider(dividerColor, ACTION_RAIL_GAP_Y, "left")}
								>
									<Box
										pointerEvents="auto"
										onClick={(e) => e.stopPropagation()}
										onMouseDown={(e) => e.stopPropagation()}
										onKeyDown={(e) => {
											if (e.key === "Enter" || e.key === " ") e.stopPropagation();
										}}
									>
										<RowActionsMenu actions={rowActionItems} row={row} />
									</Box>
								</Box>
							)}
						</Box>
					</Box>
				);
			})}

			{/* Pagination */}
			{paginationSlot ? (
				<div style={{ marginTop: "20px", display: "flex", justifyContent: "center" }}>
					{paginationSlot}
				</div>
			) : (
				pagination && (
					<div
						style={{
							display: "flex",
							justifyContent: "center",
							alignItems: "center",
							gap: "6px",
							marginTop: "20px",
						}}
					>
						{renderIconButtonWithTooltip({
							tooltip: "Previous",
							cursor: page <= 1 ? "not-allowed" : "pointer",
							disabled: page <= 1,
							"aria-label": "Previous page",
							variant: "ghost",
							onClick: () => emitPageChange(page - 1),
							children: <span aria-hidden>◀</span>,
						})}
						<Text as="span" style={{ marginLeft: "8px", marginRight: "8px" }}>
							{page} / {pageCount}
						</Text>
						{renderIconButtonWithTooltip({
							tooltip: "Next",
							cursor: page >= (pageCount || 1) ? "not-allowed" : "pointer",
							disabled: page >= (pageCount || 1),
							"aria-label": "Next page",
							variant: "ghost",
							onClick: () => emitPageChange(page + 1),
							children: <span aria-hidden>▶</span>,
						})}
					</div>
				)
			)}
		</div>
	);
}

CardList.propTypes = {
	title: PropTypes.string,
	subtitle: PropTypes.node,
	rows: PropTypes.arrayOf(PropTypes.object),
	keyField: PropTypes.string,
	columns: PropTypes.arrayOf(
		PropTypes.shape({
			key: PropTypes.string.isRequired,
			title: PropTypes.node.isRequired,
			w: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
			minW: PropTypes.number,
			align: PropTypes.oneOf(["left", "center", "right"]),
			render: PropTypes.func,
			// ── Column tooltip config (documented here for discoverability) ───────────────
			// Tooltip behavior for this column (per cell):
			// - "auto"   → only when clamped (ellipsis)
			// - "always" → even when not clamped (useful for custom content)
			// - function(row) → returns "auto" | "always" per row
			tooltip: PropTypes.oneOfType([PropTypes.string, PropTypes.func]),
			// Tooltip text override (string or function(row)). If empty/undefined, no tooltip is shown.
			tooltipText: PropTypes.oneOfType([PropTypes.func, PropTypes.string]),
		}),
	),
	onRowClick: PropTypes.func,
	onChangeRow: PropTypes.func,
	toolbar: PropTypes.shape({
		showInactive: PropTypes.shape({
			value: PropTypes.bool,
			onChange: PropTypes.func,
			label: PropTypes.node,
		}),
		sortBy: PropTypes.shape({
			valueId: PropTypes.string,
			onChange: PropTypes.func,
			options: PropTypes.array,
		}),
		pageSize: PropTypes.shape({
			valueId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
			onChange: PropTypes.func,
			options: PropTypes.array,
		}),
		actions: PropTypes.arrayOf(
			PropTypes.shape({ icon: PropTypes.node, label: PropTypes.string, onClick: PropTypes.func }),
		),
	}),
	pagination: PropTypes.shape({
		page: PropTypes.number,
		pageCount: PropTypes.number,
		onPageChange: PropTypes.func,
	}),
	emptyState: PropTypes.node,
	testId: PropTypes.string,
	showLabels: PropTypes.bool,
	density: PropTypes.oneOf(["compact", "comfortable"]),
	responsive: PropTypes.shape({
		mode: PropTypes.oneOf(["fixed", "auto"]),
		breakpointPx: PropTypes.number, // used in "fixed"
		splitAfter: PropTypes.number, // used in "fixed"
		maxRows: PropTypes.number, // used in "auto"
		minColWidth: PropTypes.number, // used in "auto"
		gap: PropTypes.number, // used in "auto"
	}),
	rowActions: PropTypes.func, // (row) => RowAction[] | null
};
